import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Child } from "@sytofood/shared";

import { ApiError, api } from "./api";
import { useAuth } from "./auth-context";
import { mealChildProfilesEnabled } from "./flags";

// ADR-0018: a household has one adult member ("self") plus zero or more
// child profiles. The "active profile" is whose dziennik the user is
// looking at right now. It is per-user (different parents on the same
// household may pin different children), persisted in localStorage so
// the choice survives reloads.

export type ActiveProfile =
  | { kind: "self" }
  | { kind: "child"; childId: string };

export type ProfileContextValue = {
  active: ActiveProfile;
  children: Child[];
  setActive: (next: ActiveProfile) => void;
  refreshChildren: () => Promise<void>;
  loading: boolean;
};

const ProfileContext = createContext<ProfileContextValue | null>(null);

const STORAGE_PREFIX = "sytofood.activeProfile.";

function readStored(userId: string): ActiveProfile {
  if (typeof window === "undefined") return { kind: "self" };
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return { kind: "self" };
    // Defensive parse: any malformed value falls back to "self" so a stale
    // localStorage write from a previous schema cannot block the user.
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "kind" in parsed &&
      (parsed as { kind: unknown }).kind === "child" &&
      "childId" in parsed &&
      typeof (parsed as { childId: unknown }).childId === "string"
    ) {
      return { kind: "child", childId: (parsed as { childId: string }).childId };
    }
    return { kind: "self" };
  } catch {
    return { kind: "self" };
  }
}

function writeStored(userId: string, value: ActiveProfile): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(value));
  } catch {
    // storage blocked — non-fatal, choice just won't persist
  }
}

export function ProfileProvider({ children: nodes }: { children: ReactNode }) {
  const { state } = useAuth();
  const user = state.status === "signed-in" ? state.user : null;
  const userId = user?.id ?? null;
  const householdId = user?.activeHouseholdId ?? null;
  const flagOn = mealChildProfilesEnabled();

  // Active profile is keyed by userId; reset to "self" when the signed-in
  // user changes (different account, sign-out then sign-in).
  const [active, setActiveState] = useState<ActiveProfile>({ kind: "self" });
  const [childrenList, setChildrenList] = useState<Child[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // Tracks the userId whose stored value has been hydrated. Without this,
  // every signed-in render rehydrates from localStorage and clobbers an
  // in-memory setActive (e.g. user picks a child then we drop back to self).
  const hydratedForRef = useRef<string | null>(null);

  // Hydrate on first sign-in for this userId.
  useEffect(() => {
    if (!userId) {
      hydratedForRef.current = null;
      setActiveState({ kind: "self" });
      setChildrenList([]);
      return;
    }
    if (hydratedForRef.current === userId) return;
    hydratedForRef.current = userId;
    setActiveState(readStored(userId));
  }, [userId]);

  // Fetch children when signed-in + household selected + flag on. We do NOT
  // gate on flag at storage level — switching the flag off should hide
  // the UI but not erase the user's pinned child.
  const refreshChildren = useCallback(async () => {
    if (!householdId || !flagOn) return;
    setLoading(true);
    try {
      const list = await api.getChildren(householdId);
      setChildrenList(list);
    } catch (err) {
      // 401 already handled by the auth flow; for everything else we
      // silently fall back to an empty list (UI shows "no profiles yet"
      // rather than blocking the whole app).
      if (err instanceof ApiError && err.status === 401) {
        setChildrenList([]);
      }
    } finally {
      setLoading(false);
    }
  }, [householdId, flagOn]);

  useEffect(() => {
    if (!householdId || !flagOn) {
      setChildrenList([]);
      return;
    }
    void refreshChildren();
  }, [householdId, flagOn, refreshChildren]);

  // Self-heal: if the active child was deleted (or filtered out), fall back
  // to "self" so the rest of the app doesn't request meals for a vanished
  // child and 404.
  useEffect(() => {
    if (active.kind !== "child") return;
    if (loading) return;
    const stillExists = childrenList.some((c) => c.id === active.childId);
    if (!stillExists) {
      setActiveState({ kind: "self" });
      if (userId) writeStored(userId, { kind: "self" });
    }
  }, [active, childrenList, loading, userId]);

  const setActive = useCallback(
    (next: ActiveProfile) => {
      setActiveState(next);
      if (userId) writeStored(userId, next);
    },
    [userId],
  );

  const value = useMemo<ProfileContextValue>(
    () => ({
      active,
      children: childrenList,
      setActive,
      refreshChildren,
      loading,
    }),
    [active, childrenList, setActive, refreshChildren, loading],
  );

  return <ProfileContext.Provider value={value}>{nodes}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used inside <ProfileProvider>");
  return ctx;
}
